import React, { useContext } from 'react';
import type { Scene } from '../../types';
import { useInlineClipboard } from './inline-clipboard-context';
import { InlineInsertContext } from './inline-insert-context';
import styles from './inline-editor.module.css';

function EyeIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 3h6v6M10 14 21 3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
      <path
        d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CutIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="6" cy="6" r="3" stroke="currentColor" strokeWidth="2" />
      <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="2" />
      <path d="M8.12 8.12 20 20M8.12 15.88 20 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

interface InlineLineControlsProps {
  blockId: string;
  scene: Scene;
  editMode: boolean;
  onOpenInEditor?: () => void;
  onPreview?: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}

export const InlineLineControls: React.FC<InlineLineControlsProps> = ({
  blockId,
  scene,
  editMode,
  onOpenInEditor,
  onPreview,
  onMoveUp,
  onMoveDown,
  onDelete,
}) => {
  const insertCtx = useContext(InlineInsertContext);
  const clipboard = useInlineClipboard();
  const insertOpen = insertCtx?.insertAfterBlockId === blockId;

  return (
    <div className={`${styles.slControls} ${!editMode ? styles.slControlsReadonly : ''}`}>
      {onOpenInEditor && (
        <button
          type="button"
          className={`${styles.slCtrlBtn} ${styles.slCtrlBtnExternal}`}
          onClick={onOpenInEditor}
          title="Ouvrir dans l'éditeur Scènes"
        >
          <ExternalLinkIcon />
        </button>
      )}
      {onPreview && (
        <button
          type="button"
          className={`${styles.slCtrlBtn} ${styles.slCtrlBtnPreview}`}
          onClick={onPreview}
          title="Aperçu à partir d'ici"
        >
          <EyeIcon />
        </button>
      )}
      {editMode && (
        <>
      {clipboard && (
        <>
          <button
            type="button"
            className={`${styles.slCtrlBtn} ${styles.slCtrlBtnCopy}`}
            onClick={() => clipboard.copyBlock(scene)}
            title="Copier le bloc"
          >
            <CopyIcon />
          </button>
          <button
            type="button"
            className={`${styles.slCtrlBtn} ${styles.slCtrlBtnCut}`}
            onClick={() => {
              clipboard.copyBlock(scene);
              onDelete();
            }}
            title="Couper le bloc"
          >
            <CutIcon />
          </button>
        </>
      )}
      <button type="button" className={styles.slCtrlBtn} onClick={onMoveUp} title="Monter">
        ↑
      </button>
      <button type="button" className={styles.slCtrlBtn} onClick={onMoveDown} title="Descendre">
        ↓
      </button>
      {insertCtx && (
        <button
          type="button"
          className={`${styles.slCtrlBtn} ${styles.slCtrlBtnAdd}${insertOpen ? ` ${styles.slCtrlBtnActive}` : ''}`}
          onClick={() => insertCtx.requestInsertAfter(blockId)}
          title="Insérer un bloc en dessous"
        >
          +
        </button>
      )}
      <button
        type="button"
        className={`${styles.slCtrlBtn} ${styles.slCtrlBtnDel}`}
        onClick={onDelete}
        title="Supprimer"
      >
        ✕
      </button>
        </>
      )}
    </div>
  );
};
